<template>
  <section class="auth-form__section terms-agreement">
    <h2 v-if="!hideTitle" class="auth-form__section-title">약관 동의</h2>

    <p class="terms-agreement__notice">
      {{ hideTitle ? '필수만 동의하셔도 가입됩니다. 만 14세는 본인 확인용입니다.' : `${COMPANY.serviceName} 서비스 이용을 위해 아래 약관에 동의해 주세요.` }}
    </p>

    <div class="auth-form__agreements">
      <label class="auth-form__agree-all">
        <input
          v-model="allChecked"
          type="checkbox"
          class="auth-form__checkbox"
        />
        전체 동의
        <span class="terms-agreement__all-note">필수·선택 항목을 포함합니다.</span>
      </label>

      <div
        v-for="item in agreementItems"
        :key="item.id"
        class="terms-agreement__item"
      >
        <label class="terms-agreement__label">
          <input
            :checked="modelValue[item.id]"
            type="checkbox"
            class="auth-form__checkbox"
            @change="onItemChange(item.id, ($event.target as HTMLInputElement).checked)"
          />
          <span class="terms-agreement__badge" :class="item.required ? 'terms-agreement__badge--required' : 'terms-agreement__badge--optional'">
            {{ item.required ? '필수' : '선택' }}
          </span>
          <span class="terms-agreement__text">{{ item.label }}</span>
        </label>
        <button
          v-if="item.docType"
          type="button"
          class="terms-agreement__view"
          @click="openDoc(item.docType)"
        >
          내용 보기
        </button>
      </div>
    </div>

    <TermsModal
      :open="modalOpen"
      :title="activeDoc?.title ?? ''"
      :updated-at="activeDoc?.updatedAt"
      :sections="activeSections"
      :privacy-rows="activePrivacyRows"
      @close="modalOpen = false"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import TermsModal from '@/components/auth/TermsModal.vue'
import {
  COMPANY,
  LEGAL_DOCS,
  MARKETING_CONSENT_SECTIONS,
  PRIVACY_COLLECTION_ROWS,
  PRIVACY_CONSENT_SECTIONS,
  TERMS_OF_SERVICE_SECTIONS,
  type LegalDocType,
} from '@/constants/legalContent'

export type AgreementId = 'age' | 'terms' | 'privacy' | 'marketing'

export type AgreementState = Record<AgreementId, boolean>

const props = defineProps<{
  modelValue: AgreementState
  hideTitle?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: AgreementState]
}>()

const agreementItems = [
  { id: 'age' as const, label: '만 14세 이상입니다.', required: true },
  { id: 'terms' as const, label: '이용약관 동의', required: true, docType: 'service' as LegalDocType },
  { id: 'privacy' as const, label: '개인정보 수집·이용 동의', required: true, docType: 'privacy-consent' as LegalDocType },
  { id: 'marketing' as const, label: '마케팅·광고성 정보 수신 동의', required: false, docType: 'marketing' as LegalDocType },
]

const modalOpen = ref(false)
const activeDocType = ref<LegalDocType | null>(null)

const allChecked = computed({
  get: () => agreementItems.every((item) => props.modelValue[item.id]),
  set: (checked: boolean) => {
    const next = { ...props.modelValue }
    agreementItems.forEach((item) => {
      next[item.id] = checked
    })
    emit('update:modelValue', next)
  },
})

const activeDoc = computed(() => (activeDocType.value ? LEGAL_DOCS[activeDocType.value] : null))

const activeSections = computed(() => {
  if (!activeDocType.value) return []
  if (activeDocType.value === 'service') return TERMS_OF_SERVICE_SECTIONS
  if (activeDocType.value === 'privacy-consent') return PRIVACY_CONSENT_SECTIONS
  if (activeDocType.value === 'marketing') return MARKETING_CONSENT_SECTIONS
  return []
})

const activePrivacyRows = computed(() =>
  activeDocType.value === 'privacy-consent' ? PRIVACY_COLLECTION_ROWS : undefined,
)

const onItemChange = (id: AgreementId, checked: boolean) => {
  emit('update:modelValue', { ...props.modelValue, [id]: checked })
}

const openDoc = (docType: LegalDocType) => {
  activeDocType.value = docType
  modalOpen.value = true
}
</script>

<style lang="scss" src="./TermsAgreementSection.scss"></style>
